/**
 * Страница политики конфиденциальности `/privacy` (Фаза 3, И3).
 *
 * Статичная страница (шаблон — [`About`](./About.tsx)): какие данные собираются,
 * зачем, где хранятся, cookies/сессия, права пользователя и контакты. Минимальный
 * внятный текст; помечена как черновик для последующего согласования с юристом —
 * НЕ является юридической консультацией.
 *
 * Ссылка на /privacy есть в форме регистрации (обязательный чекбокс согласия).
 *
 * i18n: для абзацев со строчными <strong>/<code>/<a> используется компонент <Trans>
 * (react-i18next) — перевод остаётся цельным предложением, разметка вставляется по
 * индексу компонентов (<0>…</0>). en/cn — placeholder/машинный перевод.
 */
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '../components/ui';

/** Контакт для вопросов/запросов на удаление данных (placeholder — заменить на реальный). */
export const PRIVACY_CONTACT_EMAIL = 'privacy@kikayoo.com';

export function Privacy(): JSX.Element {
  const { t } = useTranslation();
  return (
    <main className="card card--page" aria-labelledby="privacy-title">
      <Link to="/" className="back">
        {t('nav.back')}
      </Link>
      <p className="block-subtitle">{t('privacy.sectionLabel')}</p>
      <h1 id="privacy-title" className="page-title">
        {t('privacy.title')}
      </h1>
      <p className="muted small" data-testid="privacy-disclaimer">
        {t('privacy.disclaimer')}
      </p>

      <section className="block" aria-labelledby="privacy-data-title">
        <h2 id="privacy-data-title" className="block-title">
          {t('privacy.dataTitle')}
        </h2>
        <ul className="muted">
          <li>
            <Trans i18nKey="privacy.dataAccount" components={[<strong />]} />
          </li>
          <li>
            <Trans i18nKey="privacy.dataPlayerAnswers" components={[<strong />]} />
          </li>
          <li>
            <Trans i18nKey="privacy.dataService" components={[<strong />]} />
          </li>
          <li>
            <Trans i18nKey="privacy.dataImages" components={[<strong />]} />
          </li>
          <li>
            <Trans i18nKey="privacy.dataLogs" components={[<strong />]} />
          </li>
        </ul>
      </section>

      <section className="block" aria-labelledby="privacy-purpose-title">
        <h2 id="privacy-purpose-title" className="block-title">
          {t('privacy.purposeTitle')}
        </h2>
        <p className="muted">{t('privacy.purposeBody')}</p>
      </section>

      <section className="block" aria-labelledby="privacy-storage-title">
        <h2 id="privacy-storage-title" className="block-title">
          {t('privacy.storageTitle')}
        </h2>
        <p className="muted">{t('privacy.storageBody')}</p>
      </section>

      <section className="block" aria-labelledby="privacy-cookies-title">
        <h2 id="privacy-cookies-title" className="block-title">
          {t('privacy.cookiesTitle')}
        </h2>
        <p className="muted">
          <Trans i18nKey="privacy.cookiesBody" components={[<code />]} />
        </p>
      </section>

      <section className="block" aria-labelledby="privacy-rights-title">
        <h2 id="privacy-rights-title" className="block-title">
          {t('privacy.rightsTitle')}
        </h2>
        <p className="muted">{t('privacy.rightsBody')}</p>
      </section>

      <section className="block" aria-labelledby="privacy-contact-title">
        <h2 id="privacy-contact-title" className="block-title">
          {t('privacy.contactTitle')}
        </h2>
        <p className="muted">
          <Trans
            i18nKey="privacy.contactBody"
            components={[<a className="footer-link" href={`mailto:${PRIVACY_CONTACT_EMAIL}`} />]}
            values={{ email: PRIVACY_CONTACT_EMAIL }}
          />
        </p>
      </section>
    </main>
  );
}
